import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import Button from 'flarum/common/components/Button';
import DiscussionFilters from './components/DiscussionFilters';
import extractText from 'flarum/common/utils/extractText';

// Adds a blue "Discussions du forum" header bar directly above the
// discussion list, matching champis.net/home's own boxed list-group look
// (see forum.less - the header, the sort/filter toolbar, and .DiscussionList
// are styled as one continuous bordered card via a general-sibling
// selector, so nothing here needs to know about DiscussionList's own
// markup). Shown on both the homepage and tag/category pages (IndexPage is
// shared between both routes) with the same static title - not the tag's
// own name - per the request to match the homepage's card exactly.
//
// Priority 105 - above core's own 'toolbar' item (100, the sort/filter
// row) - so this is the very first thing in the left column, lining up
// with the search widget being the very first thing in the right column
// (see addSpeciesSearch.js's own priority 200 - both are "first in their
// column" regardless of the exact number).
export default function redesignIndexPage() {
  extend(IndexPage.prototype, 'contentItems', function (items) {
    // Homepage only (matching champis.net's own funnel-filter widget,
    // which is likewise specific to that page's forum-list box, not shown
    // on tag/category pages) - same `tags` route param guard already used
    // by addHomeDescriptions.js for the same homepage-vs-tag-page split.
    const isHomepage = !m.route.param('tags');
    items.add('champisDiscussionListHeader', m("h2", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-comments",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-forum.forum.index.discussions_header')), 105);

    // Below core's own 'toolbar' (100, the sort dropdown) - see
    // components/DiscussionFilters.js for why it needs its own line
    // beneath both rather than sharing the header/toolbar's row.
    if (isHomepage) {
      items.add('champisDiscussionFilters', m(DiscussionFilters, null), 95);
    }
  });

  // The funnel toggle itself lives in core's own actionItems() - the same
  // extensible list that produces the toolbar's search/refresh/"mark all
  // as read" buttons (search=110, refresh/markAllAsRead default to 0) -
  // rather than a bespoke element placed elsewhere, so it's a real sibling
  // of the refresh button and picks up the exact same look for free (the
  // translucent-white .Button treatment further down already targets
  // *any* .Button inside .IndexPage-toolbar, not just core's own). 50
  // lands it right between search and refresh, i.e. next to refresh
  // regardless of whether "mark all as read" (logged-in only) also shows.
  extend(IndexPage.prototype, 'actionItems', function (items) {
    if (m.route.param('tags')) return;
    items.add('champisDiscussionFilterToggle', m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-filter",
      "aria-label": extractText(app.translator.trans('champis-forum.forum.index.filter_toggle_accessible_label')),
      title: extractText(app.translator.trans('champis-forum.forum.index.filter_toggle_accessible_label')),
      onclick: e => {
        // Plain DOM toggle rather than component state: IndexPage gates
        // its own re-render on a SubtreeRetainer tracking app.discussions
        // specifically (same class of issue as DiscussionListItem's own
        // retainer, see extend.php's species field docblock) - a click
        // here doesn't touch that, so Mithril silently never re-invokes
        // contentItems()/actionItems() again and a state-driven show/hide
        // never reaches the DOM. The filter row itself is always mounted
        // (see DiscussionFilters.js) so there's a real element to toggle
        // a class on directly. `.active` (not a bespoke modifier) reuses
        // the same "pressed" look the toolbar's own .Button rule already
        // defines for that class.
        e.currentTarget.classList.toggle('active');
        document.querySelector('.ChampisDiscussionFilters')?.classList.toggle('ChampisDiscussionFilters--hidden');
      }
    }), 50);
  });
}